import React, { useState, useId } from 'react';
import { cn } from '@/utils/cn';
import {
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  error?: string;
  success?: boolean;
  helpText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  showClear?: boolean;
  showCharCount?: boolean;
  containerClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      success,
      helpText,
      leftIcon,
      rightIcon,
      showClear = false,
      showCharCount = false,
      type = 'text',
      disabled,
      required,
      className,
      containerClassName,
      maxLength,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const inputId = useId();
    const helpTextId = useId();
    const errorId = useId();

    const [showPassword, setShowPassword] = useState(false);
    const [internalValue, setInternalValue] = useState('');

    // Use controlled or uncontrolled value
    const currentValue = value !== undefined ? value : internalValue;
    const currentLength =
      typeof currentValue === 'string' ? currentValue.length : String(currentValue || '').length;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (value === undefined) {
        setInternalValue(e.target.value);
      }
      onChange?.(e);
    };

    const handleClear = () => {
      const syntheticEvent = {
        target: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>;

      if (value === undefined) {
        setInternalValue('');
      }
      onChange?.(syntheticEvent);
    };

    const hasError = Boolean(error);
    const hasValue = currentLength > 0;
    const showPasswordToggle = type === 'password';
    const inputType = showPasswordToggle && showPassword ? 'text' : type;

    // Determine which icon to show on the right
    const getRightContent = () => {
      if (hasError) {
        return <ExclamationCircleIcon className="w-5 h-5 text-danger" aria-hidden="true" />;
      }
      if (success) {
        return <CheckCircleIcon className="w-5 h-5 text-success" aria-hidden="true" />;
      }
      if (showPasswordToggle) {
        return (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="p-1 hover:bg-muted rounded transition-colors"
            tabIndex={-1}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <EyeSlashIcon className="w-5 h-5 text-foreground/60" />
            ) : (
              <EyeIcon className="w-5 h-5 text-foreground/60" />
            )}
          </button>
        );
      }
      if (showClear && hasValue && !disabled) {
        return (
          <button
            type="button"
            onClick={handleClear}
            className="p-1 hover:bg-muted rounded transition-colors"
            tabIndex={-1}
            aria-label="Clear input"
          >
            <XMarkIcon className="w-5 h-5 text-foreground/60" />
          </button>
        );
      }
      if (rightIcon) {
        return <span className="text-foreground/60" aria-hidden="true">{rightIcon}</span>;
      }
      return null;
    };

    const describedBy = [
      helpText ? helpTextId : null,
      error ? errorId : null,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={cn('w-full', containerClassName)}>
        {/* Label */}
        <label
          htmlFor={inputId}
          className="block text-sm font-medium mb-1.5 text-foreground"
        >
          {label}
          {required && (
            <span className="text-danger ml-1" aria-label="required">
              *
            </span>
          )}
        </label>

        {/* Input Container */}
        <div className="relative">
          {/* Left Icon */}
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-foreground/60">
              {leftIcon}
            </div>
          )}

          {/* Input */}
          <input
            ref={ref}
            id={inputId}
            type={inputType}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            required={required}
            maxLength={maxLength}
            spellCheck={false}
            autoComplete="off"
            aria-invalid={hasError}
            aria-describedby={describedBy || undefined}
            className={cn(
              'w-full h-11 px-3 rounded-md border',
              'bg-background text-foreground',
              'transition-colors duration-200',
              'placeholder:text-foreground/40',
              'focus:outline-none focus:ring-3 focus:ring-primary/50',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              leftIcon && 'pl-10',
              (rightIcon ||
                showPasswordToggle ||
                (showClear && hasValue) ||
                hasError ||
                success) &&
                'pr-10',
              hasError && 'border-danger focus:ring-danger/50',
              success && !hasError && 'border-success focus:ring-success/50',
              !hasError && !success && 'border-border',
              className
            )}
            {...props}
          />

          {/* Right Content */}
          {getRightContent() && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
              {getRightContent()}
            </div>
          )}
        </div>

        {/* Help Text / Error / Character Count */}
        <div className="mt-1.5 flex items-start justify-between gap-2">
          <div className="flex-1">
            {error && (
              <p id={errorId} className="text-sm text-danger" role="alert">
                {error}
              </p>
            )}
            {!error && helpText && (
              <p id={helpTextId} className="text-sm text-foreground/60">
                {helpText}
              </p>
            )}
          </div>

          {showCharCount && maxLength && (
            <p
              className={cn(
                'text-sm',
                currentLength > maxLength * 0.9 ? 'text-warning' : 'text-foreground/60'
              )}
              aria-live="polite"
            >
              {currentLength}/{maxLength}
            </p>
          )}
        </div>
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
